import { DateFilter, datePeriodEnumToJSON, DateFilterType, monthEnumToJSON, quarterEnumToJSON, GrowthBenchmarkType } from "build-ts/relative_date";
import { PlanEdgeType } from "../containers/batch/Batch";
import { epochConverterSecond } from "./dateUtils";
import { AggregationType, aggregationTypeToJSON, AnalysisColumnType, analysisColumnTypeToJSON } from "build-ts/enums";
import { FilterProto, FilterProto_CalculatedDateRange } from "build-ts/metadata";
import { getOperatorSymbol } from "./analysisUtils";
import { WELCOME_CHAT_MESSAGES } from "constants/messages";

export const generateStepName = (elements: string[]): string => {

  // Handle cases where elements is undefined, not an array, or empty
  if (!elements || !Array.isArray(elements) || elements.length === 0) {
    return "";
  }

  // 1. Safely take the metric from the first element, if it exists
  const metric = elements[0] || "";

  // 2. Safely process the second element, handle cases where it's undefined or empty
  if (!elements[1]) {
    return metric;
  }

  const items = elements[1].split(",").map(item => item.trim()).filter(item => item !== "");
  const processedItems = items.map(item => {
    const itemParts = item.split(" ");

    // If it's a single word, take up to the first three characters
    if (itemParts.length === 1) {
      return itemParts[0].slice(0, 3);
    }
    // If it's a two-word item, safely take the first two letters of the first word and the first letter of the second word
    else if (itemParts.length >= 2) {
      const firstWord = itemParts[0];
      const secondWord = itemParts[1];
      const firstPart = firstWord.slice(0, 2) || "";  // Ensure first two letters of first word
      const secondPart = secondWord.charAt(0) || "";  // Ensure first letter of second word
      return `${firstPart}${secondPart}`;
    }
    // Handle cases with more than two words (if needed)
    return itemParts.map(part => part.charAt(0)).join("");
  });

  // Combine all parts
  const result = `${metric} | ${processedItems.join("/")}`;
  return result;
};

export const getDateFilterName = (dateFilter: DateFilter): string => {
  if (!dateFilter) {
    return "";
  }

  if (dateFilter.type === undefined || dateFilter.type === DateFilterType.UNRECOGNIZED) {
    return "Invalid";
  }

  // Helper function to get "as of" date string using epochConverterSecond or current date
  const getAsOfDateString = (): string => {
    if (dateFilter.reference_date_epoc_sec) {
      return epochConverterSecond(dateFilter.reference_date_epoc_sec);
    }
    return "";
  };

  switch (dateFilter.type) {
    case DateFilterType.LAST_N_PERIOD: {
      const asOfDateStr = getAsOfDateString();
      const period = dateFilter.date_period !== undefined
        ? datePeriodEnumToJSON(dateFilter.date_period).charAt(0)
        : "";
      const number = dateFilter.number ?? "";
      return asOfDateStr ? `Last ${number}${period} (as of ${asOfDateStr})` : `Last ${number}${period}`;
    }
    case DateFilterType.NEXT_N_PERIOD: {
      const asOfDateStr = getAsOfDateString();
      const period = dateFilter.date_period !== undefined
        ? datePeriodEnumToJSON(dateFilter.date_period).charAt(0)
        : "";
      const number = dateFilter.number ?? "";
      return asOfDateStr ? `Next ${number}${period} (as of ${asOfDateStr})` : `Next ${number}${period}`;
    }
    case DateFilterType.THIS_PERIOD: {
      const asOfDateStr = getAsOfDateString();
      const period = dateFilter.date_period !== undefined
        ? datePeriodEnumToJSON(dateFilter.date_period).toLowerCase()
        : "";
      return asOfDateStr ? `This ${period} (as of ${asOfDateStr})` : `This ${period}`;
    }
    case DateFilterType.EXACT_DATE_RANGE: {
      if (dateFilter.date_range?.low_epoch_sec === undefined ||
        dateFilter.date_range?.high_epoch_sec === undefined) {
        return "Invalid Date Range";
      }
      const lowDate = epochConverterSecond(dateFilter.date_range.low_epoch_sec);
      const highDate = epochConverterSecond(dateFilter.date_range.high_epoch_sec);
      if (lowDate === highDate) {
        return `${lowDate}`;
      }
      return `Between ${lowDate} to ${highDate}`;
    }
    case DateFilterType.EXACT_DATE: {
      if (dateFilter.reference_date_epoc_sec === undefined) {
        return "Invalid Date";
      }
      const exactDate = epochConverterSecond(dateFilter.reference_date_epoc_sec);
      return `${exactDate}`;
    }
    case DateFilterType.PERIOD_TO_DATE: {
      const asOfDateStr = getAsOfDateString();
      const period = dateFilter.date_period !== undefined
        ? datePeriodEnumToJSON(dateFilter.date_period).charAt(0)
        : "";
      return asOfDateStr ? `${period}TD (as of ${asOfDateStr})` : `${period}TD`;
    }
    case DateFilterType.MONTH_YEAR: {
      const monthStr = monthEnumToJSON(dateFilter.month!);
      const monthFormatted =
        monthStr.charAt(0) + monthStr.slice(1, 3).toLowerCase();
      return `${monthFormatted} ${dateFilter.year}`;
    }
    case DateFilterType.QUARTER_YEAR: {
      if (dateFilter.quarter === undefined || dateFilter.year === undefined) {
        return "Invalid Quarter/Year";
      }
      const quarterStr = quarterEnumToJSON(dateFilter.quarter);
      return `${quarterStr} ${dateFilter.year}`;
    }
    case DateFilterType.YEAR_ONLY: {
      if (dateFilter.year === undefined) {
        return "Invalid Year";
      }
      return `${dateFilter.year}`;
    }
    default:
      return "Unknown Date Range type";
  }
};

export const getTreeName = (currentName: string, dateFilter: DateFilter): string => {

  // Split the current name by '|' and trim each part
  const nameParts = currentName.split("|").map(part => part.trim());

  if (nameParts.length === 3) {
    // Update the last part with the new temporal name
    nameParts[2] = getDateFilterName(dateFilter);
  } else if (nameParts.length === 2) {
    // Append the temporal name as the third part
    nameParts.push(getDateFilterName(dateFilter));
  } else {
    // Handle cases where the name does not have 2 or 3 parts
    // For example, if it's a single part, you might want to append the temporal name
    // Adjust this logic based on your specific requirements
    nameParts.push(getDateFilterName(dateFilter));
  }

  // Join the parts back together with ' | ' delimiter
  return nameParts.join(" | ");
};

// Function to get the  type
export const getAnalysisPlanNodeName = (edgeName: string, edgeType: string) => {
  switch (edgeType) {
    case "ATTRIBUTE_EDGE":
      return `Split by ${edgeName}`;
    case "BENCHMARK_EDGE":
      return `Compare to ${edgeName}`;
    case "FUNDAMENTAL_EDGE":
      return `Explore ${edgeName}`;
    default:
      return "Unknown Edge Type";
  }
};

// Mapping of edge types to labels based on analysis type
const edgeLabelMap: Record<PlanEdgeType, Record<string, string>> = {
  "Metric->Benchmark": {
    Growth: "Next",
    Diagnostic: "Next",
  },
  "Benchmark->First": {
    Growth: "Next",
    Diagnostic: "Next",
  },
  "After": {
    Growth: "Pick Outliers",
    Diagnostic: "Pick At Risk",
  },
};

// Function to get the label based on edge type and analysis type
export const getAnalysisPlanEdgeLabel = (
  edgeType: PlanEdgeType,
  analysisType: string
): string => {
  // If analysisType is not in the map, fall back to 'Growth'
  const validAnalysisType = edgeLabelMap[edgeType]?.[analysisType]
    ? analysisType
    : "Growth";

  return edgeLabelMap[edgeType][validAnalysisType] || "Unknown Edge";
};

/**
 * Gets the string names of an enum, excluding specific keys.
 * 
 * @param enumObj - The enum object to process.
 * @returns A list of enum string names excluding specified keys.
 */
export const getEnumNames = <T extends { [key: string]: number | string }>(enumObj: T): string[] => {
  const excludedKeys = ["UNRECOGNIZED"];
  return Object.keys(enumObj)
    .filter((key) => isNaN(Number(key)))
    .filter((key) => !excludedKeys.includes(key));
};

/**
 * Returns a label for a given growth benchmark type.
 * 
 * @param growthBenchmarkType - The type of growth benchmark. Determines the format of the label.
 * @param number - (Optional) A numeric value representing the number of periods for aggregation.
 * @param aggregationType - (Optional) The type of aggregation to be used. 
 * @returns A string label for the growth benchmark:
 *          - "Prior Period" for .GrowthBenchmarkType.PRIOR_PERIOD.
 *          - "Last N Periods AggregationType" for GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION.
 */
export const getBenchmarkLabel = (
  growthBenchmarkType: GrowthBenchmarkType,
  number?: number,
  aggregationType?: AggregationType
): string => {
  switch (growthBenchmarkType) {
    case GrowthBenchmarkType.PRIOR_PERIOD:
      return "Prior Period";
    case GrowthBenchmarkType.PRIOR_YEAR:
      return "Prior Year";
    case GrowthBenchmarkType.LAST_N_PERIOD_AGGREGATION:
      const aggregation = aggregationTypeToJSON(aggregationType!);
      return `Last ${number ?? "N"} Periods ${aggregation.charAt(0) + aggregation.slice(1).toLowerCase()}`;
    default:
      return "Unknown Benchmark";
  }
};

/**
 * Returns the formatted header.
 * @param {string} str name of table header.
 * @returns formatted header.
 */
export const formatHeaderName = (str: string): string => {
  if (!str) return "";
  return str
    .replace(/_/g, " ")        // Replace underscores with spaces
    .toLowerCase()              // Convert the entire string to lowercase
    .split(/[\s-]+/)            // Split on spaces or hyphens
    .map(word => {
      if (word.length === 0) return word;  // Handle empty strings
      if (!isNaN(Number(word))) return word;  // If the word is a number, return it as is
      return word.toUpperCase();  // Return the word in all caps
    })
    .join(" ")
    .replace(/ - /g, "-");  // Reformat space-dash-space into just dash
};

/**
 * Maps a given AnalysisColumnType to its display string representation.
 *
 * @param columnType - The column type to be converted to a display string.
 * @returns A string representation of the column type for display purposes.
 */
export const getAnalysisColumnTypeDisplay = (columnType: AnalysisColumnType): string => {
  const typeMap: Partial<Record<AnalysisColumnType, string>> = {
    [AnalysisColumnType.MELTED_VARIABLE]: "VARIABLE",
    [AnalysisColumnType.MELTED_VALUE]: "VALUE",
    [AnalysisColumnType.LAST_N_PERIOD_AVERAGE]: "4-PERIOD AVG",
    [AnalysisColumnType.DIAGNOSTIC_STATUS]: "STATUS",
    [AnalysisColumnType.COMPOSITION]: "COMPOSITION",
    [AnalysisColumnType.VARIANCE]: "VARIANCE",
    [AnalysisColumnType.VARIANCE_PERCENTAGE]: "VARIANCE%",
    [AnalysisColumnType.VARIANCE_CONTRIBUTION]: "CONTRIBUTION",
    [AnalysisColumnType.NOT_ANALYSIS_COLUMN]: "UNKNOWN",
  };

  return typeMap[columnType] ?? analysisColumnTypeToJSON(columnType);
};

/**
 * Returns the label for a given analysis column type.
 *
 * @param type - The column type to be converted to a display string.
 * @returns A string representation of the column type for display purposes.
 */
export const getAnalysisColumnTypeLabel = (type: AnalysisColumnType): string => {
  // Ensure we're handling undefined or null
  if (type === undefined || type === null) {
    return "Unknown";
  }

  // Map enum values to display labels
  switch (type) {
    case AnalysisColumnType.COMPOSITION:
      return "Composition";
    case AnalysisColumnType.VARIANCE:
      return "Variance";
    case AnalysisColumnType.VARIANCE_PERCENTAGE:
      return "Variance Percentage";
    case AnalysisColumnType.VARIANCE_CONTRIBUTION:
      return "Variance Contribution";
    case AnalysisColumnType.NORMALISED_TARGET:
      return "Normalised Target";
    case AnalysisColumnType.ACCOMPLISH_PERCENTAGE:
      return "Accomplish Percentage";
    case AnalysisColumnType.COMPOSITION_PERCENTAGE:
      return "Composition Percentage";
    case AnalysisColumnType.RANK:
      return "Rank";
    case AnalysisColumnType.NOT_ANALYSIS_COLUMN:
      return "Not Analysis Column";
    default:
      return `Type ${type}`;
  }
};

/**
 * Creates UI specific filter values string to display.
 *
 * @param filter - FilterProto.
 * @returns A string representation of the filter.
 */
export const getFilterDisplayString = (filter: FilterProto): string => {
  if (filter?.date_filter) {
    return getDateFilterName(filter?.date_filter!);
  } else if (filter?.values?.length > 0) {
    return filter.values.join(", ");
  } else if (filter?.calculated_date_ranges?.length > 0) {
    return filter.calculated_date_ranges
      .map((date: FilterProto_CalculatedDateRange) => {
        const operatorSymbol = getOperatorSymbol(date?.operator!);
        const formattedDate = epochConverterSecond(date?.date_epoch_sec!);
        return `${operatorSymbol} ${formattedDate}`;
      })
      .join(", ");
  } else {
    return "Selected Values";
  }
};

/**
 * @returns list of column type to display in Plan page.
 */
export const analysisColumnTypes = [
  AnalysisColumnType.COMPOSITION,
  AnalysisColumnType.VARIANCE,
  AnalysisColumnType.VARIANCE_PERCENTAGE,
  AnalysisColumnType.VARIANCE_CONTRIBUTION,
  AnalysisColumnType.DIAGNOSTIC_STATUS,
  AnalysisColumnType.NORMALISED_TARGET,
];

/**
 * Converts an input string from uppercase with underscores (e.g., "LAST_WEEK")
 * to sentence case (e.g., "Last week").
 *
 * - Replaces underscores with spaces
 * - Converts the entire string to lowercase
 * - Capitalizes only the first letter
 *
 * @param input - The input string to format.
 * @returns A formatted string in sentence case.
 */
export const formatToSentenceCase = (input: string | null | undefined): string => {
  if (typeof input !== "string") return "";

  // Replace underscores with spaces and convert to lowercase
  const sentence = input.replace(/_/g, " ").toLowerCase();

  // Capitalize only the first letter
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

/**
 * Generates a random suffix of a given length.
 *
 * @param length - The length of the suffix.
 * @returns A random suffix.
 */
export const generateRandomSuffix = (length: number): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Helper function to convert a string to a seed
// It is an DJB2 hash function
const stringToSeed = (str: string): number => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
};

/**
 * Returns a action verb for the given action type from the ACTION_VERBS object.
 * 
 * @param actionType - The type of action to get a verb for.
 * @param name - The name to use for the random number generator.
 * @param index - The index to use for the random number generator.
 * @returns A action verb for the given action type.
 */
export const getRandomActionVerb = (verbs: string[], name: string, index: number): string => {
  const nameSeed = stringToSeed(name);
  const combinedSeed = nameSeed + index; // Combine name and index
  return verbs[combinedSeed % verbs.length];
};

/**
 * Returns a random welcome message from the ACTION_VERBS object.
 *
 * @returns A random welcome message.
 */

export const getRandomWelcomeMessage = (): string => {
  return WELCOME_CHAT_MESSAGES[Math.floor(Math.random() * WELCOME_CHAT_MESSAGES.length)];
};
